import React, { useEffect, useState } from "react";
import { api, fmtCompact, pdi, pdiTier } from "../lib/api";
import { Crosshair, Eye, Warning } from "@phosphor-icons/react";

const TIER_CLASS = {
  GREEN: "tier-GREEN",
  YELLOW: "tier-YELLOW",
  ORANGE: "tier-ORANGE",
  RED: "tier-RED",
  BLACK: "tier-BLACK",
};

const TIER_ORDER = ["GREEN", "YELLOW", "ORANGE", "RED", "BLACK"];

export default function RiskSentinel({ onOpenRiskProof }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.riskSentinel(20).then(setData).catch(() => setData({ suppliers: [], tier_distribution: {} }));
  }, []);

  return (
    <section
      data-testid="risk-sentinel"
      className="bg-slate-900 border border-slate-800 flex flex-col h-full"
    >
      <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h2 className="font-display text-base font-semibold text-slate-50 flex items-center gap-2">
            <Crosshair weight="duotone" className="text-emerald-400" size={16} />
            PDI Sentinel · Counterparty Intelligence
          </h2>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">
            PDI (Procurement Decision Intelligence) 0–100 · weighted by SEC filings, exclusions, concentration · high = stronger play
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-1" data-testid="tier-distribution">
            {TIER_ORDER.map((t) => (
              <div
                key={t}
                title={`${t}: ${data.tier_distribution[t] || 0} suppliers`}
                className={`h-6 px-2 flex items-center text-[10px] font-mono font-semibold rounded-sm ${TIER_CLASS[t]}`}
              >
                {data.tier_distribution[t] || 0}
              </div>
            ))}
          </div>
        )}
      </header>

      <div className="overflow-auto thin-scroll max-h-[520px] divide-y divide-slate-800">
        {!data && Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="p-4 skeleton h-16" />
        ))}
        {data && data.suppliers.map((s) => (
          <div
            key={s.supplier_id}
            data-testid={`risk-row-${s.supplier_id}`}
            className="px-5 py-3 hover:bg-slate-800/50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <span
                className={`shrink-0 inline-flex items-center justify-center w-12 h-12 font-mono font-bold rounded-sm tier-${pdiTier(s.risk_score)}`}
                data-testid={`pdi-badge-${s.supplier_id}`}
                title={`PDI ${pdi(s.risk_score)} · Procurement Decision Intelligence`}
              >
                {pdi(s.risk_score)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-slate-50 truncate">{s.supplier}</span>
                  <span className="font-mono text-[10px] text-slate-500 shrink-0">
                    {s.state} · CAGE {s.cage_code}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                  <span className={`tracking-mono-label text-[10px] ${tierTextClass(pdiTier(s.risk_score))}`}>
                    PDI · {pdiTier(s.risk_score)}
                  </span>
                  <span className="font-mono text-slate-500">·</span>
                  <span className="font-mono">{s.active_pos} active POs</span>
                  <span className="font-mono text-slate-500">·</span>
                  <span className="font-mono text-emerald-400">{fmtCompact(s.exposure)} exposure</span>
                  {s.sec_filing_status?.includes("Going Concern") && (
                    <>
                      <span className="font-mono text-slate-500">·</span>
                      <span className="inline-flex items-center gap-1 text-amber-400">
                        <Warning weight="fill" size={11} />
                        Going Concern
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={() => onOpenRiskProof(s)}
                data-testid={`open-risk-proof-${s.supplier_id}`}
                className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-mono-label text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-500/60 rounded-sm"
              >
                <Eye weight="bold" size={12} /> Proof
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function tierTextClass(tier) {
  return {
    GREEN: "text-emerald-400",
    YELLOW: "text-amber-400",
    ORANGE: "text-orange-400",
    RED: "text-rose-400",
    BLACK: "text-rose-300",
  }[tier];
}
